import React from 'react';
import { useTranslation } from 'next-i18next';
import classnames from 'classnames';

import * as Notifications from 'common/Notification';
import { Button } from 'components/ui';
import { useOrderMutation } from 'hooks/data/useOrderMutation';
import { useAnalytics } from 'hooks/useAnalytics';

import type { ButtonProps } from 'components/ui';

import type { IOrder, IOrderCouponInfo, IOrderSummaryLine } from 'types';

import styles from './OrderSummary.module.scss';

interface Props {
  className?: string,
  rowClassName?: string,
  rowValueClassName?: string,
  rowKeyClassName?: string,
  separatorClassName?: string,
  rowKeyContainerClassName?: string,
  deleteBtnClassName?: string,
  deleteBtnProps?: Partial<ButtonProps>,
  order?: IOrder
}

const OrderSummary = (props: Props) => {

  const {
    className,
    rowClassName,
    rowValueClassName,
    rowKeyClassName,
    separatorClassName,
    rowKeyContainerClassName,
    deleteBtnClassName,
    deleteBtnProps,
    order
  } = props;

  const { summary = [], couponInfo } = order || {};

  return (
    <div className={className}>
      {
        summary.map((row, index) => {
          const { type, ...rest } = row;
          switch (type) {
            case ROW_TYPES.SEPARATOR:
              return <Separator className={separatorClassName} key={`${index}-separator`} />;
            case ROW_TYPES.DEFAULT:
              return (
                <Row
                  key={rest?.key?.text || index}
                  data={rest}
                  className={rowClassName}
                  valueClassName={rowValueClassName}
                  keyClassName={rowKeyClassName}
                  keyContainerClassName={rowKeyContainerClassName}
                  deleteBtnClassName={deleteBtnClassName}
                  deleteBtnProps={deleteBtnProps}
                  couponInfo={couponInfo}
                />
              );
            default:
              return null;
          }
        })
      }
    </div>
  );
};

const Separator = ({ className }: {className?: string}) => <div className={classnames(className)} />;

interface RowProps {
  data?: Omit<IOrderSummaryLine, 'type'>,
  className?: string,
  valueClassName?: string,
  keyClassName?: string,
  keyContainerClassName?: string,
  deleteBtnClassName?: string,
  deleteBtnProps?: Partial<ButtonProps>,
  couponInfo?: IOrderCouponInfo
}

const Row = (props: RowProps) => {
  const {
    data,
    className,
    valueClassName,
    keyClassName,
    keyContainerClassName,
    deleteBtnClassName,
    deleteBtnProps = {},
    couponInfo
  } = props;

  const { deleteSummaryItem, isLoading: isMutationLoading } = useOrderMutation();
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const { key, value, size = '' } = data || {};

  const {
    text: keyText, bold: keyBold, style: keyStyle = '', deleteKey
  } = key || {};

  const {
    text: valueText, bold: valueBold, style: valueStyle = ''
  } = value || {};

  const keyClasses = classnames(
    keyClassName,
    { bold: keyBold },
    `color-${keyStyle}`
  );

  const keyContainerClasses = classnames(
    keyContainerClassName,
    styles.keyContainer
  );

  const valueClasses = classnames(
    valueClassName,
    { bold: valueBold },
    `color-${valueStyle}`
  );

  const rowClasses = classnames(
    className,
    `size-${size}`
  );

  const handleDeleteItem = () => {
    const { code: voucherCode } = couponInfo || {};
    if (deleteKey) {
      deleteSummaryItem({
        path: deleteKey,
        onError: () => Notifications.showError(t('ERRORS.DEFAULT_TOAST'), { autoClose: 2000, toastId: 'deleteSummaryItem' }),
        onSuccess: () => {
          // Workaround to detect if this is for deleting a voucher (as this is controlled dynamically form the backend)
          if (deleteKey?.includes(DELETE_KEY_MATCHER_REMOVE_VOUCHER) && voucherCode) {
            analytics.removeVoucher(voucherCode);
          }
        }
      });
    }
  };

  return (
    <div className={rowClasses}>
      <div className={keyContainerClasses}>
        <div className={keyClasses}>
          {keyText}
        </div>
        {
          deleteKey && (
            <Button
              size="small"
              icon="x"
              iconSize={16}
              iconStrokeWidth={3}
              className={classnames(styles.deleteBtn, deleteBtnClassName)}
              onClick={handleDeleteItem}
              disabled={isMutationLoading}
              loading={isMutationLoading}
              {...deleteBtnProps}
            />
          )
        }
      </div>
      <div className={valueClasses}>
        {valueText}
      </div>
    </div>
  );
};

const DELETE_KEY_MATCHER_REMOVE_VOUCHER = '/remove-discount/voucher';

const ROW_TYPES = {
  DEFAULT: 'default',
  SEPARATOR: 'separator'
};

export default OrderSummary;
